import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, CheckCircle2, XCircle, Clock, ExternalLink, Building2, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PlanBadge from "@/components/PlanBadge";
import IdChip from "@/components/IdChip";

/**
 * SA Vendor Approval Queue (Prompt 3).
 *
 * Lists every vendor application. Tabs filter by status. Approve mints a
 * `role='vendor'` user + emails a password-set magic-link. Reject captures
 * an optional reason and emails the applicant.
 */
export default function SuperAdminVendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState("pending_approval");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyVendorId, setBusyVendorId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [detailVendor, setDetailVendor] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/super-admin/vendors${statusFilter ? `?status=${statusFilter}` : ""}`)
      .then(({ data }) => {
        setVendors(data.vendors || []);
        setCounts(data.counts || {});
      })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail) || err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const decide = async (vendorId, decision, reason) => {
    setBusyVendorId(vendorId);
    setError(null);
    try {
      const { data } = await api.post(`/super-admin/vendors/${vendorId}/decision`, {
        decision, reason: reason || undefined,
      });
      setRejectingId(null);
      setRejectReason("");
      alert(
        decision === "approve"
          ? `Approved. Magic-link email ${data.invite_sent ? "sent to" : "queued for"} the vendor.`
          : "Application marked rejected. Applicant notified by email."
      );
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyVendorId(null);
    }
  };

  const deleteVendor = async (vendor) => {
    const confirmMsg =
      `Permanently delete "${vendor.company_name}"?\n\n` +
      `This will remove the vendor record, their user account, and any outstanding\n` +
      `password/activation tokens. This action is irreversible.`;
    if (!window.confirm(confirmMsg)) return;
    setBusyVendorId(vendor.vendor_id);
    setError(null);
    try {
      await api.delete(`/super-admin/vendors/${vendor.vendor_id}`);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusyVendorId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F4]" data-testid="sa-vendors-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/super-admin" className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#1A422D]">
          <ChevronLeft size={14} /> Platform Admin
        </Link>

        <div className="mt-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]">
              Vendor Approval Queue
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A422D] mt-2">Vendor Applications</h1>
          </div>
        </div>

        {/* Status tabs */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="sa-vendors-tabs">
          <StatusTab active={statusFilter} value="pending_approval" count={counts.pending_approval} onClick={setStatusFilter}>
            <Clock size={14} /> Pending
          </StatusTab>
          <StatusTab active={statusFilter} value="approved" count={counts.approved} onClick={setStatusFilter}>
            <CheckCircle2 size={14} /> Approved
          </StatusTab>
          <StatusTab active={statusFilter} value="rejected" count={counts.rejected} onClick={setStatusFilter}>
            <XCircle size={14} /> Rejected
          </StatusTab>
          <StatusTab active={statusFilter} value="" count={(counts.pending_approval || 0) + (counts.approved || 0) + (counts.rejected || 0)} onClick={setStatusFilter}>
            All
          </StatusTab>
        </div>

        {error && (
          <div className="mt-6 px-4 py-3 border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a] text-sm">{error}</div>
        )}

        <div className="mt-8">
          {loading ? (
            <div className="overline">Loading…</div>
          ) : vendors.length === 0 ? (
            <div className="p-10 bg-white border border-[#E5E3D8] text-center text-[#7A7A7A]">
              <Building2 size={30} className="mx-auto text-[#B59A5A]" />
              <div className="mt-4 font-serif text-2xl text-[#1A422D]">No vendors in this state.</div>
              <div className="text-sm mt-2">Applications will appear here as they arrive.</div>
            </div>
          ) : (
            <div className="space-y-4">
              {vendors.map((v) => (
                <VendorRow
                  key={v.vendor_id}
                  vendor={v}
                  busy={busyVendorId === v.vendor_id}
                  onApprove={() => decide(v.vendor_id, "approve")}
                  onReject={() => { setRejectingId(v.vendor_id); setRejectReason(""); }}
                  onDelete={() => deleteVendor(v)}
                  onOpen={() => setDetailVendor(v)}
                  showActions={statusFilter === "pending_approval" || v.status === "pending_approval"}
                  rejecting={rejectingId === v.vendor_id}
                  rejectReason={rejectReason}
                  setRejectReason={setRejectReason}
                  onConfirmReject={() => decide(v.vendor_id, "reject", rejectReason)}
                  onCancelReject={() => { setRejectingId(null); setRejectReason(""); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {detailVendor && (
        <VendorDetailModal vendor={detailVendor} onClose={() => setDetailVendor(null)} />
      )}
    </div>
  );
}

function StatusTab({ active, value, count, onClick, children }) {
  const on = active === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      data-testid={`sa-vendors-tab-${value || "all"}`}
      className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.22em] font-semibold transition-colors border ${
        on
          ? "bg-[#1A422D] text-[#F9F8F4] border-[#1A422D]"
          : "bg-white text-[#4A4A4A] border-[#E5E3D8] hover:border-[#1A422D]"
      }`}
    >
      {children}
      {count !== undefined && (
        <span className={`px-1.5 py-0.5 text-[10px] ${on ? "bg-[#D9C68A]/25 text-[#D9C68A]" : "bg-[#F9F8F4] text-[#4A4A4A]"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function VendorRow({ vendor, busy, onApprove, onReject, onDelete, onOpen, showActions, rejecting, rejectReason, setRejectReason, onConfirmReject, onCancelReject }) {
  return (
    <div className="p-6 bg-white border border-[#E5E3D8]" data-testid={`sa-vendor-row-${vendor.vendor_id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-serif text-xl text-[#1A422D]">{vendor.company_name}</h3>
            <StatusPill status={vendor.status} />
            {vendor.plan && <PlanBadge plan={vendor.plan} />}
          </div>
          <div className="mt-2 text-sm text-[#4A4A4A]">
            {vendor.primary_contact_name} · <a href={`mailto:${vendor.email}`} className="text-[#1A422D] underline">{vendor.email}</a> · {vendor.phone}
          </div>
          <div className="mt-1 text-xs text-[#7A7A7A]">
            {(vendor.product_categories || []).slice(0, 3).join(" · ")}{vendor.product_categories?.length > 3 ? " …" : ""}
          </div>
          {vendor.status === "approved" && (
            <div className="mt-2">
              <IdChip
                value={vendor.vendor_id}
                label="Vendor ID"
                testId={`sa-vendor-id-${vendor.vendor_id}`}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onOpen}
            data-testid={`sa-vendor-open-${vendor.vendor_id}`}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#E5E3D8] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A] hover:border-[#1A422D] hover:text-[#1A422D]"
          >
            <ExternalLink size={13} /> Open
          </button>
          {showActions && !rejecting && (
            <>
              <button
                type="button"
                onClick={onApprove}
                disabled={busy}
                data-testid={`sa-vendor-approve-${vendor.vendor_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1A422D] text-[#F9F8F4] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#0F2A1C] disabled:opacity-50"
              >
                <CheckCircle2 size={13} /> {busy ? "…" : "Approve"}
              </button>
              <button
                type="button"
                onClick={onReject}
                disabled={busy}
                data-testid={`sa-vendor-reject-${vendor.vendor_id}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#A33] text-[#A33] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#A33] hover:text-white disabled:opacity-50"
              >
                <XCircle size={13} /> Reject
              </button>
            </>
          )}
          {/* iter69c · Permanent delete — available on every status so SA can
              wipe test/duplicate vendor records without keeping stale rows. */}
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            data-testid={`sa-vendor-delete-${vendor.vendor_id}`}
            title="Permanently delete this vendor"
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#8a1a1a] text-[#8a1a1a] text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#8a1a1a] hover:text-white disabled:opacity-50"
          >
            <Trash2 size={13} /> {busy ? "…" : "Delete"}
          </button>
        </div>
      </div>

      {rejecting && (
        <div className="mt-4 p-4 bg-[#fbe9e9] border border-[#f2b0b0]" data-testid={`sa-vendor-reject-panel-${vendor.vendor_id}`}>
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#8a1a1a]">Reason (optional)</div>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={3}
            data-testid={`sa-vendor-reject-reason-${vendor.vendor_id}`}
            className="mt-2 w-full input-field"
            placeholder="Shared with the applicant in the rejection email."
          />
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onConfirmReject}
              disabled={busy}
              data-testid={`sa-vendor-reject-confirm-${vendor.vendor_id}`}
              className="px-4 py-2 bg-[#A33] text-white text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#8a1a1a] disabled:opacity-50"
            >
              {busy ? "Rejecting…" : "Confirm Reject"}
            </button>
            <button
              type="button"
              onClick={onCancelReject}
              className="px-4 py-2 border border-[#E5E3D8] text-[11px] uppercase tracking-[0.22em] font-semibold text-[#4A4A4A] hover:border-[#1A422D]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_approval: { label: "Pending", cls: "bg-[#D9C68A]/25 text-[#8A6A2A] border-[#B59A5A]" },
    approved: { label: "Approved", cls: "bg-[#1A422D]/10 text-[#1A422D] border-[#1A422D]/40" },
    rejected: { label: "Rejected", cls: "bg-[#fbe9e9] text-[#8a1a1a] border-[#f2b0b0]" },
    deactivated: { label: "Deactivated", cls: "bg-[#eee] text-[#4A4A4A] border-[#ccc]" },
  };
  const m = map[status] || map.pending_approval;
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function VendorDetailModal({ vendor, onClose }) {
  const [rotating, setRotating] = useState(false);
  const [rotateResult, setRotateResult] = useState(null);
  const [rotateErr, setRotateErr] = useState("");

  const rotatePasskey = async () => {
    if (!window.confirm(
      `Clear ${vendor.company_name}'s vendor back-office passkey?\n\n` +
      "Their team will lose passkey access immediately, and we'll email a " +
      "temp login code to " + (vendor.email || "the vendor") + " so they " +
      "can sign in and set a new one."
    )) return;
    setRotating(true); setRotateErr(""); setRotateResult(null);
    try {
      const { data } = await api.post(
        `/super-admin/vendors/${vendor.vendor_id}/back-office/rotate`
      );
      setRotateResult(data);
    } catch (e) {
      setRotateErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRotating(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="sa-vendor-detail-modal"
    >
      <div
        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#E5E3D8]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#B59A5A]">Vendor Application</div>
              <h2 className="font-serif text-3xl text-[#1A422D] mt-1">{vendor.company_name}</h2>
            </div>
            <button type="button" onClick={onClose} className="text-[#7A7A7A] hover:text-[#1A422D] text-3xl leading-none">×</button>
          </div>

          <Row label="Website">{vendor.website || "—"}</Row>
          <Row label="Primary Contact">{vendor.primary_contact_name} · {vendor.email}</Row>
          <Row label="Phone">{vendor.phone}</Row>
          <Row label="Business Address">{vendor.business_address}</Row>
          <Row label="Years in Business">{vendor.years_in_business || "—"}</Row>
          <Row label="Product Categories">{(vendor.product_categories || []).join(", ") || "—"}</Row>
          <Row label="Sales Territories">{(vendor.sales_territories || []).join(", ") || "—"}</Row>
          <Row label="Description">
            <div className="whitespace-pre-wrap text-sm">{vendor.company_description}</div>
          </Row>
          {vendor.rejection_reason && <Row label="Rejection Reason">{vendor.rejection_reason}</Row>}
          <Row label="Applied">{new Date(vendor.created_at).toLocaleString()}</Row>

          {/* iter72b · SA vendor account actions */}
          {vendor.status === "approved" && (
            <Row label="Vendor Portal Access">
              <div className="text-sm text-[#4A4A4A]">
                Passkey configured: <strong>{vendor.back_office_set_at ? "Yes" : "No"}</strong>
                {vendor.back_office_set_at && (
                  <span className="text-[#7A7A7A]"> · set {new Date(vendor.back_office_set_at).toLocaleDateString()}</span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={rotatePasskey}
                  disabled={rotating}
                  data-testid="sa-vendor-rotate-passkey"
                  className="inline-flex items-center gap-1.5 border border-[#A33] text-[#8a1a1a] hover:bg-[#8a1a1a] hover:text-[#F9F8F4] px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {rotating ? "Clearing…" : "Rotate / Clear Passkey"}
                </button>
                {rotateResult && (
                  <span className="text-[11px] text-[#1A422D] inline-flex items-center gap-1" data-testid="sa-vendor-rotate-flash">
                    <CheckCircle2 size={12} /> Passkey cleared
                    {rotateResult.temp_code_sent ? " · temp code emailed" : " (email deferred)"}
                  </span>
                )}
              </div>
              {rotateErr && (
                <div className="mt-2 text-xs text-[#8a1a1a] border border-[#A33] bg-[#fbe9e9] px-2 py-1.5">
                  {rotateErr}
                </div>
              )}
            </Row>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="mt-5 pt-5 border-t border-[#F0EDE0] first:border-0 first:mt-6 first:pt-0">
      <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#7A7A7A]">{label}</div>
      <div className="mt-1 text-[#1A1A1A]">{children}</div>
    </div>
  );
}
